// @flow
import type {SagaIterator} from 'redux-saga';
import Store from 'store';
import {FULFILLED} from 'redux-promise-middleware';

import {GDL, APP, ANALYTICS, CUSTOMER, CART, QUOTE_ITEM} from 'constants/actions';
import type {
    CartDataForGlobalDataLayerType,
    CategoryDataForGlobalDataLayerType,
    ProductDataForGlobalDataLayerType,
    SuccessDataForGlobalDataLayerType,
} from 'constants/flow/flowTypesGlobalDataLayer';

import {takeLatest, takeEvery, select, call, put, cancel, all, take} from 'redux-saga/effects';
import {getCustomer as getCustomerState} from 'selectors/customer/customer';
import {getGaClientIdOutsideCall} from 'libs/analytics/refactoringAnalytics';
import {getCurrentPage} from 'selectors/app/app';
import {getRouterResolver} from 'sagas/globalDataLayer/router/router';
import {getTypePage} from 'sagas/analytics/helpers/getTypePage';
import {getToken} from 'reducers/oAuth/oAuth';
import {addAccessory} from 'pages/cart/store/actions/accessories/accessories';

import {getFieldsForGlobalDataLayer} from './globalDataLayerBase';
import {
    resolveProductGlobalDataLayer,
    resolveCartGlobalDataLayer,
} from './globalDataLayerResolvers';

/**
 * Get saved fields about customer from store
 */
export function getCustomerFieldsFromStore() {
    const globalDataLayer = window.GlobalDataLayer || {};
    globalDataLayer.email = Store.get('email') ? Store.get('email') : '';
    globalDataLayer.md5 = Store.get('md5') ? Store.get('md5') : '';
    globalDataLayer.sha1 = Store.get('sha1') ? Store.get('sha1') : '';
    globalDataLayer.sha256 = Store.get('sha256') ? Store.get('sha256') : '';
    globalDataLayer.first_name = Store.get('first_name') ? Store.get('first_name') : '';
    globalDataLayer.last_name = Store.get('last_name') ? Store.get('last_name') : '';
    window.GlobalDataLayer = globalDataLayer;
}

/**
 * Save fields about customer into local storage
 */
export function saveDataIntoStore() {
    if ('email' in window.GlobalDataLayer && window.GlobalDataLayer.email.length > 0) {
        localStorage.setItem('email', window.GlobalDataLayer.email);
        localStorage.setItem('md5', window.GlobalDataLayer.md5);
        localStorage.setItem('sha1', window.GlobalDataLayer.sha1);
        localStorage.setItem('sha256', window.GlobalDataLayer.sha256);
        localStorage.setItem('first_name', window.GlobalDataLayer.first_name || '');
        localStorage.setItem('last_name', window.GlobalDataLayer.last_name || '');
    }
}

/**
 * Return base fields for global data layer
 * @returns {SagaIterator} base fields
 */
export function* getBaseFields(): SagaIterator {
    // TODO: This selector not necessary with normal router
    const page = yield select(getCurrentPage);
    if (!page.page) yield cancel();
    const customer = yield select(getCustomerState);
    const data = yield call(getFieldsForGlobalDataLayer, customer);
    data.client_id_ga = yield call(getGaClientIdOutsideCall);
    return data;
}

/**
 * Fill Global Data Layer.
 *
 * @param {SagaIterator} data - Data for page.
 */
export function* fillGlobalDataLayer(
    data:
        | ProductDataForGlobalDataLayerType
        | CartDataForGlobalDataLayerType
        | SuccessDataForGlobalDataLayerType
        | CategoryDataForGlobalDataLayerType,
): SagaIterator {
    const globalDataLayer = {};
    const customer = yield select(getCustomerState);
    const baseFields = yield call(getBaseFields, customer);

    window.GlobalDataLayer = yield {...globalDataLayer, ...baseFields, ...data};
    yield put({type: GDL.GLOBAL_DATA_LAYER_FULFILLED});

    if (window.GlobalDataLayer.email && window.GlobalDataLayer.email.length)
        yield call(saveDataIntoStore);
}

/**
 * Fill Global Data Layer from local storage.
 */
export function* fillGlobalDataLayerFromLocalStorage(): SagaIterator {
    const customer = yield call(getCustomerFieldsFromStore);

    if (customer) {
        const globalDataLayer = window.GlobalDataLayer || {};
        const baseFields = yield call(getBaseFields, customer);
        window.GlobalDataLayer = {...globalDataLayer, ...baseFields};
    }
}

/**
 * Preparation Global Data Layer for recording.
 *
 * @param {Array<function(): SagaIterator>} resolveGlobalDataLayer - Global Data Layer resolver.
 * @param {function(): SagaIterator} waitForSagaBeforeResolve - Wait function.
 */
export function* preparationGlobalDataLayer(
    resolveGlobalDataLayer: Array<SagaIterator>,
    waitForSagaBeforeResolve: SagaIterator,
): SagaIterator {
    if (Array.isArray(waitForSagaBeforeResolve) && waitForSagaBeforeResolve.length) {
        yield all(waitForSagaBeforeResolve.map(call));
    }
    const data = yield call(resolveGlobalDataLayer);

    yield call(fillGlobalDataLayer, data);
}

/**
 * Get Global Data Layer resolver.
 *
 * @returns {SagaIterator} - Global Data Layer resolver.
 */
export function* getGlobalDataLayerResolver(): SagaIterator {
    const page = yield call(getTypePage);
    return yield call(getRouterResolver, page);
}

/**
 * On location change handler.
 */
export function* onLocationChange(): SagaIterator {
    const {resolveGlobalDataLayer, waitForSagaBeforeResolve} = yield call(
        getGlobalDataLayerResolver,
    );

    yield call(fillGlobalDataLayerFromLocalStorage);
    yield call(preparationGlobalDataLayer, resolveGlobalDataLayer, waitForSagaBeforeResolve);
}

/**
 * Update Global Data Layer after login/registration.
 */
export function* updateGlobalDataLayerAfterLogin(): SagaIterator {
    yield take(`${CUSTOMER.GET_CUSTOMER_DATA}_${FULFILLED}`);
    yield call(preparationGlobalDataLayer, resolveProductGlobalDataLayer);
}

/**
 * Update Global Data Layer
 */
export function* updateGlobalDataLayerForCart(): SagaIterator {
    yield take(`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`);
    yield call(preparationGlobalDataLayer, resolveCartGlobalDataLayer);
}

/**
 * Begin of saga
 */
export default function* globalDataLayerSaga(): SagaIterator {
    yield takeLatest(APP.SAVE_LOCATION, onLocationChange);
    yield takeEvery(
        [
            `${CART.REMOVE_QUOTE_ITEM}_${FULFILLED}`,
            `${CART.CHANGE_ITEM_QTY}_${FULFILLED}`,
            `${CART.CHANGE_LENSES_ITEM_QTY}_${FULFILLED}`,
            `${CART.ADD_UPSELL_ITEM}_${FULFILLED}`,
            `${CART.REMOVE_UPSELL_ITEM}_${FULFILLED}`,
            `${addAccessory}_${FULFILLED}`,
        ],
        updateGlobalDataLayerForCart,
    );
    yield takeEvery(
        ANALYTICS.PDP_ATTR_CHANGE,
        preparationGlobalDataLayer,
        resolveProductGlobalDataLayer,
    );
    yield takeEvery(getToken, updateGlobalDataLayerAfterLogin);
}
